import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { hasPeriodEnded } from '../closeHistoricalPeriod';
import { Calendar, AlertTriangle, Lock, Unlock, Clock, CreditCard, BookOpen } from 'lucide-react';
import { cadenceLabelForPeriod } from '../../../../../utils/vatCadence';

const VatModernHeader = ({
    vatPeriod,
    totalVat,
    undoingClose,
    handleUndoClose,
    handleVatPeriodClose,
    loading,
    currentStep,
    // Every verifikat this period has produced, so they are reachable from any
    // step rather than only from the completed record step (owner, 2026-09-02).
    verifications = [],
    onVerificationClick
}) => {
    // Prefer stored vat_total (set during XML generation) over live totalVat
    // After the zeroing verification, live totalVat is zero but vat_total has the correct declared amount
    // S4 — never a confident zero. `vat_total` is null until the report is
    // generated and `totalVat` is undefined until granska has computed, and the
    // old `|| 0` turned both unknowns into "0 kr" while granska showed a real
    // amount. An unknown amount shows no chip; a computed 0 still shows 0.
    //
    // A1 — `vat_total` is NOT NULL DEFAULT 0.00 in the tenant schema, so a
    // never-reviewed period stores a PLACEHOLDER zero that reads as knowledge:
    // Sevim helår 2025 showed "0 kr" while granska computed 17 886 kr. A stored
    // amount is only knowledge once something wrote it deliberately — the
    // reviewed/generated snapshot, a close, or an externally filed figure.
    // Before that, a stored 0 is treated as unknown and the live computed
    // amount (if any) is shown instead.
    const storedTotal = vatPeriod?.vat_total;
    const storedIsDeliberate = Boolean(
        vatPeriod?.vat_report_snapshot_sha256
        || vatPeriod?.state === 'closed'
        || Number(vatPeriod?.handled)
        || Number(vatPeriod?.externally_filed)
    );
    const hasStoredTotal = storedTotal !== null && storedTotal !== undefined && storedTotal !== ''
        && (Number(storedTotal) !== 0 || storedIsDeliberate);
    const hasComputedTotal = totalVat !== null && totalVat !== undefined;
    const amountKnown = hasStoredTotal || hasComputedTotal;
    const displayAmount = hasStoredTotal ? Number(storedTotal) : Number(totalVat || 0);

    // S3 — the chip read `vatPeriod.period_type`, a column that does not exist
    // on vat_reporting_periods, so it always fell through to "Kvartalsvis",
    // including on a helår period. The mapping lives in utils/vatCadence: it is
    // rule-table knowledge, and the rule ids it compares against must not sit in
    // the UI tree.
    const cadenceLabel = cadenceLabelForPeriod(vatPeriod);

    const formatCurrency = (amount) => {
        const numAmount = Number(amount) || 0;
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(numAmount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('sv-SE');
    };

    const isOverdue = () => {
        if (!vatPeriod || vatPeriod.state !== 'open') return false;
        return new Date(vatPeriod.due_date) < new Date();
    };

    const isClosed = vatPeriod?.state === 'closed';
    const isFrozenForSubmission = isClosed && !vatPeriod?.handled && !vatPeriod?.record_completed;

    return (
        <>
            <style>{`
                .vat-header {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1.25rem 1.5rem;
                    margin-bottom: 1.5rem;
                    color: #1e293b;
                }
                .vat-title {
                    font-size: 1.35rem;
                    font-weight: 600;
                    margin-bottom: 0.75rem;
                    display: flex;
                    align-items: center;
                    gap: 0.625rem;
                    color: #1e293b;
                }
                .vat-badges {
                    display: flex;
                    gap: 0.5rem;
                    align-items: center;
                    flex-wrap: wrap;
                }
                .vat-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.375rem;
                    padding: 0.3rem 0.625rem;
                    border-radius: 16px;
                    font-size: 0.75rem;
                    font-weight: 500;
                    white-space: nowrap;
                }
                .vat-badge-open { background: #f0fdf4; color: var(--primary-dark); border: 1px solid var(--rgba-primary-2); }
                /* A closed period is the wizard's successful end state, so it is
                   not painted like vat-badge-overdue and vat-badge-amount-pay,
                   which is what it used to share (#fee2e2 / --bs-danger). */
                .vat-badge-closed { background: #f0fdf4; color: var(--primary); border: 1px solid var(--rgba-primary-2); }
                .vat-badge-due { background: var(--rgba-primary-1); color: var(--primary); border: 1px solid var(--rgba-primary-3); }
                .vat-badge-overdue { background: #fef2f2; color: var(--bs-danger); border: 1px solid #fecaca; }
                .vat-badge-amount-pay { background: #fef2f2; color: var(--bs-danger); border: 1px solid #fecaca; }
                .vat-badge-amount-refund { background: #f0fdf4; color: var(--primary); border: 1px solid var(--rgba-primary-2); }
                .vat-badge-neutral { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
                .vat-badge-verification {
                    background: #f1f5f9;
                    color: #475569;
                    border: 1px solid #e2e8f0;
                    cursor: pointer;
                    font: inherit;
                }
                .vat-badge-verification:hover {
                    border-color: var(--rgba-primary-3, #cfe3d3);
                    color: var(--primary);
                }

                .vat-closed-note {
                    display: flex;
                    align-items: center;
                    background: var(--rgba-primary-1, #f4f8f5);
                    border: 1px solid var(--rgba-primary-3, #cfe3d3);
                    border-radius: 10px;
                    padding: 0.7rem 0.9rem;
                    font-size: 0.85rem;
                    color: #475569;
                }
                @media (max-width: 768px) {
                    .vat-header { padding: 1rem; }
                    .vat-title { font-size: 1.1rem; }
                }
            `}</style>

            <div className="vat-header">
                <div className="d-flex justify-content-between align-items-start">
                    <h1 className="vat-title">
                        <Calendar size={22} color="var(--primary)" />
                        Momsrapport {vatPeriod && formatDate(vatPeriod.start_date)} — {vatPeriod && formatDate(vatPeriod.end_date)}
                    </h1>
                    {/* Not offered while the period is still running: the server
                        refuses that with VAT_PERIOD_NOT_ELAPSED, so the button
                        could only ever produce an error. */}
                    {!isClosed && handleVatPeriodClose && currentStep === 'instructions'
                        && hasPeriodEnded(vatPeriod) && (
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleVatPeriodClose}
                            disabled={loading || undoingClose}
                            style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                        >
                            Stäng period (redan bokförd)
                        </Button>
                    )}
                </div>

                <div className="vat-badges">
                    {/* Status */}
                    <span className={`vat-badge ${isClosed ? 'vat-badge-closed' : 'vat-badge-open'}`}>
                        {isClosed ? <Lock size={12} /> : <Unlock size={12} />}
                        {isFrozenForSubmission ? 'Rapport låst' : isClosed ? 'Stängd' : 'Öppen'}
                    </span>

                    {/* Due date */}
                    <span className={`vat-badge ${isOverdue() ? 'vat-badge-overdue' : 'vat-badge-due'}`}>
                        {isOverdue() ? <AlertTriangle size={12} /> : <Clock size={12} />}
                        Deklarera & betala senast {vatPeriod && formatDate(vatPeriod.due_date)}
                        {isOverdue() && ' — försenad!'}
                    </span>

                    {/* Amount */}
                    {amountKnown && (
                        <span className={`vat-badge ${displayAmount > 0 ? 'vat-badge-amount-pay' : displayAmount < 0 ? 'vat-badge-amount-refund' : 'vat-badge-neutral'}`}>
                            <CreditCard size={12} />
                            {displayAmount > 0 ? 'Att betala: ' : displayAmount < 0 ? 'Att få tillbaka: ' : ''}
                            {formatCurrency(Math.abs(displayAmount))}
                        </span>
                    )}

                    {/* Period type */}
                    {cadenceLabel && (
                        <span className="vat-badge vat-badge-neutral">{cadenceLabel}</span>
                    )}

                    {/* One chip per verifikat the period has produced. They used
                        to appear only inside the completed record step, which
                        showed the dragning verifikat and nothing else. */}
                    {verifications.filter(Boolean).map((v) => (
                        <button
                            key={v.verification_id}
                            type="button"
                            className="vat-badge vat-badge-verification"
                            onClick={() => onVerificationClick && onVerificationClick(v)}
                            title={v.label}
                        >
                            <BookOpen size={12} />
                            {v.series}{v.number}
                        </button>
                    ))}
                </div>

                {isClosed && (
                    /* Not `Alert variant="warning"`. A closed period is a normal,
                       successful end state — the ⚠ and this stylesheet's amber
                       (#FFAA2B on pale orange) told the user something had gone
                       wrong. The consequential thing here is the UNDO, not the
                       state, so the state is calm and the action is what stands
                       out. */
                    <div className="vat-closed-note mt-3">
                        <Lock size={15} className="me-2 flex-shrink-0" style={{ color: 'var(--primary)' }} />
                        <div className="flex-grow-1">
                            {isFrozenForSubmission
                                ? 'Rapportunderlaget är låst till den genererade XML-filen. Du kan fortsätta med inlämning, betalning och bokföring.'
                                : 'Perioden är stängd. Öppna den igen om något behöver ändras.'}
                        </div>
                        <Button
                            variant="link"
                            size="sm"
                            onClick={handleUndoClose}
                            disabled={undoingClose}
                            className="p-0 ms-3 flex-shrink-0"
                        >
                            {isFrozenForSubmission ? 'Öppna och ogiltigförklara XML-filen' : 'Ångra stängning'}
                        </Button>
                        {undoingClose && <Spinner animation="border" size="sm" className="ms-2" />}
                    </div>
                )}
            </div>
        </>
    );
};

export default VatModernHeader;
